import { useEffect, useState } from 'react';

import { builderLocale } from '../i18n';

/**
 * How long an upload has been going, said out loud.
 *
 * WHY (founder, 2026-09-15). A percentage cannot carry this story. `xhr.upload.onprogress` counts bytes
 * handed to the SOCKET, not bytes the server received, so on a file of a few hundred KB the kernel takes
 * the whole body at once and the bar fills in a single jump — then everything after it, which is the
 * ENTIRE real transfer on a slow uplink, looks frozen. Her screenshot is that exact frame: a full bar and
 * a word, unchanged for minutes.
 *
 * Elapsed time is the one number that is always true and always moving. It cannot tell her how much is
 * left — nothing in a browser can — but it tells her it is still working, and it turns "this is broken"
 * into "my connection is slow today", which is the difference between closing the tab and waiting.
 *
 * NO DICTIONARY KEY is involved on purpose. `Intl` formats the unit from CLDR, so the builder's seven
 * languages (and the Arabic numerals with them) are served without a phrase to translate — and without
 * touching the seven locale files, which another session is editing right now.
 */

/** Below this an upload is simply "quick", and a counter would be noise. */
const QUIET_MS = 2_000;

/**
 * Seconds since `startedAt`, re-rendered once a second. Returns 0 (falsy — draw nothing) for the first
 * couple of seconds, so a fast upload never flashes a stopwatch on its way past.
 *
 * The timer stops with the component. The registry's job outlives every panel, so this is deliberately
 * NOT stored on the job: only something on screen needs to tick.
 */
export function useElapsedSeconds(startedAt: number | undefined): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (startedAt === undefined) return;
    const id = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(id);
  }, [startedAt]);
  if (startedAt === undefined) return 0;
  const ms = Math.max(0, now - startedAt);
  return ms < QUIET_MS ? 0 : Math.round(ms / 1000);
}

/**
 * `12` → "12 s" / "12 sec" / "12 Sek." / "١٢ ث", in the builder's own language.
 *
 * Past a minute it reads in minutes: "3 min" is what a seller acts on, "187 s" is arithmetic. Falls back
 * to a bare number if this engine lacks `style: 'unit'` — a missing suffix is a blemish, a thrown
 * exception during an upload is not acceptable.
 */
export function formatElapsed(seconds: number): string {
  const useMinutes = seconds >= 60;
  const value = useMinutes ? Math.round(seconds / 60) : seconds;
  try {
    return new Intl.NumberFormat(builderLocale(), {
      style: 'unit',
      unit: useMinutes ? 'minute' : 'second',
      unitDisplay: 'short',
    }).format(value);
  } catch {
    return String(value);
  }
}
